/* ═══════════════════════════════════════════════════
   MY FAVORITES — Aggregated favorites page
   ═══════════════════════════════════════════════════ */

if (!customElements.get("my-favorites-component")) {
  class MyFavoritesComponent extends HTMLElement {
    constructor() {
      super();
      this.customerId = this.dataset.customerId || "";
      this.globalFavKey = this.customerId
        ? `filmstudy_allfavs_${this.customerId}`
        : `filmstudy_allfavs_guest`;
    }

    connectedCallback() {
      this.render();
    }

    render() {
      const content = this.querySelector("[data-mf-content]");
      if (!content) return;

      const index = this._loadIndex();

      if (index.length === 0) {
        content.innerHTML = `
          <div class="mf-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
            <h3>No favorites yet</h3>
            <p>Browse your schemes and tap the heart icon on any play to save it here.</p>
          </div>`;
        return;
      }

      /* Group by scheme */
      const groups = new Map();
      index.forEach((entry) => {
        const key = entry.schemeId;
        if (!groups.has(key)) {
          groups.set(key, {
            schemeName: entry.schemeName || entry.schemeId,
            schemeUrl: entry.schemeUrl || "",
            items: [],
          });
        }
        groups.get(key).items.push(entry);
      });

      let html = "";

      /* Summary bar */
      const schemeCount = groups.size;
      html += `
        <div class="mf-summary">
          <span class="mf-summary-text">
            <strong>${index.length}</strong> favorite${index.length !== 1 ? "s" : ""} across <strong>${schemeCount}</strong> scheme${schemeCount !== 1 ? "s" : ""}
          </span>
          <button class="mf-clear-btn" data-mf-clear-all>Clear All</button>
        </div>`;

      /* Scheme groups */
      groups.forEach((group, schemeId) => {
        html += `
        <div class="mf-scheme-group open" data-mf-scheme="${this._esc(schemeId)}">
          <div class="mf-scheme-header" data-mf-toggle>
            <span class="mf-scheme-name">
              ${this._esc(group.schemeName)}
              <span class="mf-scheme-count">${group.items.length}</span>
            </span>
            <svg class="mf-scheme-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
          </div>
          <div class="mf-scheme-body">
            ${group.items.map((entry) => this._renderRow(entry, group.schemeUrl)).join("")}
          </div>
        </div>`;
      });

      content.innerHTML = html;
      this._bindEvents(content);
    }

    _renderRow(entry, schemeUrl) {
      const isOption = entry.type === "option";
      const name = isOption ? entry.title : entry.playName;
      const detail = isOption
        ? `${entry.formation} \u00B7 ${entry.playName}`
        : entry.formation;
      const catBadges = (entry.categories || [])
        .map((c) => `<span class="mf-category">${this._esc(c)}</span>`)
        .join("");
      const levelLabel = entry.level === "expert" ? "Expert" : "Beginner";
      const levelClass = entry.level === "expert" ? "mf-row-level--expert" : "mf-row-level--beginner";

      /* Build link to scheme page */
      const linkUrl = schemeUrl || "#";

      return `
      <div class="mf-row" data-mf-entry-id="${this._esc(entry.id)}" data-mf-scheme-id="${this._esc(entry.schemeId)}" data-mf-level="${this._esc(entry.level || "beginner")}">
        <span class="mf-row-left">
          <span class="mf-row-level ${levelClass}">${levelLabel}</span>
          <span class="mf-row-type">${isOption ? "Option" : "Play"}</span>
          ${catBadges}
          <span class="mf-row-name">${this._esc(name)}</span>
          <span class="mf-row-detail">${this._esc(detail)}</span>
        </span>
        <span class="mf-row-right">
          <button class="mf-remove-btn" data-mf-remove="${this._esc(entry.id)}" data-mf-remove-scheme="${this._esc(entry.schemeId)}" data-mf-remove-level="${this._esc(entry.level || "beginner")}" title="Remove favorite">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
          </button>
        </span>
      </div>`;
    }

    _bindEvents(content) {
      /* Toggle scheme groups */
      content.querySelectorAll("[data-mf-toggle]").forEach((hdr) => {
        hdr.addEventListener("click", () => {
          hdr.closest(".mf-scheme-group").classList.toggle("open");
        });
      });

      /* Remove single favorite */
      content.querySelectorAll("[data-mf-remove]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const entryId = btn.dataset.mfRemove;
          const schemeId = btn.dataset.mfRemoveScheme;
          const level = btn.dataset.mfRemoveLevel || "beginner";
          this._removeFavorite(entryId, schemeId, level);
          this.render();
        });
      });

      /* Clear all */
      const clearBtn = content.querySelector("[data-mf-clear-all]");
      if (clearBtn) {
        clearBtn.addEventListener("click", () => {
          if (confirm("Remove all favorites? This cannot be undone.")) {
            this._clearAll();
            this.render();
          }
        });
      }

      /* Click row → open modal */
      content.querySelectorAll(".mf-row").forEach((row) => {
        row.addEventListener("click", () => {
          const entryId = row.dataset.mfEntryId;
          const schemeId = row.dataset.mfSchemeId;
          const level = row.dataset.mfLevel || "beginner";
          const index = this._loadIndex();
          const entry = index.find((e) => e.id === entryId && e.schemeId === schemeId && e.level === level);
          if (entry) this._openModal(entry);
        });
      });
    }

    /* ── Storage ── */

    _loadIndex() {
      try {
        return JSON.parse(localStorage.getItem(this.globalFavKey)) || [];
      } catch {
        return [];
      }
    }

    _saveIndex(index) {
      try {
        localStorage.setItem(this.globalFavKey, JSON.stringify(index));
      } catch { /* quota exceeded */ }
    }

    _removeFavorite(entryId, schemeId, level) {
      /* Remove from global index */
      const index = this._loadIndex();
      const filtered = index.filter((e) => !(e.id === entryId && e.schemeId === schemeId && e.level === level));
      this._saveIndex(filtered);

      /* Also remove from per-scheme+level localStorage */
      const perSchemeKey = this.customerId
        ? `filmstudy_favs_${this.customerId}_${schemeId}_${level}`
        : `filmstudy_favs_guest_${schemeId}_${level}`;
      try {
        const schemeFavs = JSON.parse(localStorage.getItem(perSchemeKey)) || [];
        const updatedFavs = schemeFavs.filter((id) => id !== entryId);
        localStorage.setItem(perSchemeKey, JSON.stringify(updatedFavs));
      } catch { /* silent */ }
    }

    _clearAll() {
      const index = this._loadIndex();
      /* Clear each per-scheme+level key */
      const keys = new Set();
      index.forEach((e) => {
        const level = e.level || "beginner";
        const perSchemeKey = this.customerId
          ? `filmstudy_favs_${this.customerId}_${e.schemeId}_${level}`
          : `filmstudy_favs_guest_${e.schemeId}_${level}`;
        keys.add(perSchemeKey);
      });
      keys.forEach((key) => {
        try { localStorage.removeItem(key); } catch { /* silent */ }
      });
      /* Clear global index */
      this._saveIndex([]);
    }

    /* ── Modal ── */

    _openModal(entry) {
      /* Remove existing modal */
      const existing = document.querySelector(".mf-modal-overlay");
      if (existing) existing.remove();

      let bodyHTML = "";

      if (entry.type === "option") {
        bodyHTML = this._buildOptionContent(entry, entry);
      } else {
        /* Play-level: show all options with tabs if more than one */
        const options = entry.options || [];
        if (options.length > 1) {
          const firstTitle = this._esc(options[0].title || "Option 1");
          bodyHTML += `<div class="mf-option-nav" data-mf-option-nav data-total="${options.length}" data-current="0">
            <button class="mf-option-nav-arrow mf-option-nav-arrow--prev" data-mf-nav-prev aria-label="Previous option">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <span class="mf-option-nav-label" data-mf-nav-label><span class="mf-option-nav-name">${firstTitle}</span><span class="mf-option-nav-count">1 / ${options.length}</span></span>
            <button class="mf-option-nav-arrow mf-option-nav-arrow--next" data-mf-nav-next aria-label="Next option">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>`;
          bodyHTML += options.map((opt, i) =>
            `<div class="mf-modal-option${i === 0 ? " active" : ""}" data-mf-opt-panel="${i}">
              ${this._buildOptionContent(opt, entry)}
            </div>`
          ).join("");
        } else if (options.length === 1) {
          bodyHTML = this._buildOptionContent(options[0], entry);
        } else {
          bodyHTML = `<p style="opacity:0.4;text-align:center;padding:20px;">No content stored. Visit the scheme page to refresh data.</p>`;
        }
      }

      const overlay = document.createElement("div");
      overlay.className = "mf-modal-overlay";
      overlay.innerHTML = `
        <div class="mf-modal">
          <div class="mf-modal-header">
            <div class="mf-modal-header-left">
              <span class="mf-modal-scheme">${this._esc(entry.schemeName)}</span>
              <span class="mf-modal-level ${entry.level === "expert" ? "mf-modal-level--expert" : "mf-modal-level--beginner"}">${entry.level === "expert" ? "Expert" : "Beginner"}</span>
              <span class="mf-modal-formation">${this._esc(entry.formation)}</span>
              <span class="mf-modal-play-name">${this._esc(entry.playName)}</span>
            </div>
            <div class="mf-modal-header-right">
              ${entry.schemeUrl ? `<a href="${this._esc(entry.schemeUrl)}" class="mf-modal-scheme-link">
                View Scheme
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M7 7h10v10"/></svg>
              </a>` : ""}
              <button class="mf-modal-close" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
          </div>
          <div class="mf-modal-body">
            ${bodyHTML}
          </div>
        </div>
      `;

      document.body.appendChild(overlay);
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => overlay.classList.add("open"));

      /* Close handlers */
      const close = () => {
        overlay.classList.remove("open");
        document.body.style.overflow = "";
        const vid = overlay.querySelector("video");
        if (vid) vid.pause();
        setTimeout(() => overlay.remove(), 250);
      };

      overlay.querySelector(".mf-modal-close").addEventListener("click", close);
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
      });

      const escHandler = (e) => {
        if (e.key === "Escape") {
          close();
          document.removeEventListener("keydown", escHandler);
        }
      };
      document.addEventListener("keydown", escHandler);

      /* Option slider nav */
      const modalNav = overlay.querySelector("[data-mf-option-nav]");
      if (modalNav) this._bindOptionNav(modalNav, overlay.querySelector(".mf-modal-body"));
    }

    _buildOptionContent(opt, entry) {
      const hasImage = opt.image && opt.image.url;
      const hasVideo = opt.video && opt.video.url;
      let mediaHTML = "";

      if (hasImage || hasVideo) {
        const imageBlock = hasImage
          ? `<div class="mf-modal-media mf-modal-media--image"><img src="${this._esc(opt.image.url)}" alt="${this._esc(opt.title || "")}" loading="lazy"></div>`
          : "";
        const videoBlock = hasVideo
          ? `<div class="mf-modal-media mf-modal-media--video"><video src="${this._esc(opt.video.url)}" controls playsinline></video></div>`
          : "";
        if (hasImage && hasVideo) {
          mediaHTML = `<div class="mf-modal-media-grid">${imageBlock}${videoBlock}</div>`;
        } else {
          mediaHTML = imageBlock || videoBlock;
        }
      }

      const catBadges = (opt.categories || [])
        .map((c) => `<span class="mf-category">${this._esc(c)}</span>`)
        .join("");

      const title = opt.title || entry.playName || "";

      return `
        ${title ? `<div class="mf-modal-opt-title-row">
          <h4 class="mf-modal-opt-title">${this._esc(title)}</h4>
          ${catBadges ? `<div class="mf-modal-opt-cats">${catBadges}</div>` : ""}
        </div>` : ""}
        ${mediaHTML}
        <div class="mf-modal-fields">
          <div class="mf-modal-field">
            <div class="mf-modal-field-label">Setup</div>
            <div class="mf-modal-field-text">${this._esc(opt.setup || "—")}</div>
          </div>
          <div class="mf-modal-field">
            <div class="mf-modal-field-label">Reads</div>
            <div class="mf-modal-field-text">${this._esc(opt.reads || "—")}</div>
          </div>
        </div>`;
    }

    _bindOptionNav(nav, scope) {
      if (!nav || !scope) return;
      const total = parseInt(nav.dataset.total, 10);
      let current = 0;
      const label = nav.querySelector("[data-mf-nav-label]");
      const prevBtn = nav.querySelector("[data-mf-nav-prev]");
      const nextBtn = nav.querySelector("[data-mf-nav-next]");
      const panels = scope.querySelectorAll(".mf-modal-option");
      const names = [];
      panels.forEach((p, i) => {
        const title = p.querySelector(".mf-modal-opt-title");
        names.push(title ? title.textContent : `Option ${i + 1}`);
      });

      const update = () => {
        panels.forEach((p, i) => p.classList.toggle("active", i === current));
        if (label) label.innerHTML = `<span class="mf-option-nav-name">${this._esc(names[current])}</span><span class="mf-option-nav-count">${current + 1} / ${total}</span>`;
        prevBtn.classList.toggle("disabled", current === 0);
        nextBtn.classList.toggle("disabled", current === total - 1);
      };

      prevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current > 0) { current--; update(); }
      });
      nextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current < total - 1) { current++; update(); }
      });

      update();
    }

    _esc(str) {
      const d = document.createElement("div");
      d.textContent = str || "";
      return d.innerHTML;
    }

    reset() {
      this.render();
    }
  }

  customElements.define("my-favorites-component", MyFavoritesComponent);
}

/* ── Shopify editor: re-initialize on section re-render ── */
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (e) => {
    const component = e.target.querySelector("my-favorites-component");
    if (component) component.reset();
  });
}
